/**
 * 2. geçiş — ara doldurma (tam çözünürlük).
 *
 * Her piksel en yakın 3×3 hücrenin bu kare taranan noktalarına bakar.
 * Ağırlık = yakınlık × aynı yüzey mi (düzlem + normal). Başka yüzeydeki nokta sayılmaz,
 * böylece ışık koltuktan duvara, duvardan zemine taşmaz.
 *
 * Hiçbir nokta tutmazsa (ince bacak, kenar) alfa = NO_DATA: o pikseli kenar taraması
 * (refinePass) kendisi tarar.
 *
 * Çıktı: vec4( gi.rgb, ao ) ya da alfa = NO_DATA.
 */

import { Fn, If, dot, exp, float, floor, uv, vec2, vec4 } from 'three/tsl';
import { NO_DATA, readSurface, surfaceSimilarity } from './screenSurface.js';
import { cellSamplePixel } from './tracePass.js';

// Toplam ağırlık bunun altındaysa "tutan nokta yok" sayılır.
const MIN_WEIGHT = 0.05;

export function buildFillPass( node ) {

	return Fn( () => {

		const screenUv = uv();
		const pixel = floor( screenUv.mul( node._fullResolution ) );
		const center = readSurface( node, screenUv );

		const result = vec4( 0, 0, 0, 1 ).toVar(); // gökyüzü: GI yok, AO = 1

		If( center.isSky.not(), () => {

			// Pikselin en yakın olduğu taranan nokta hangi hücrede?
			const nearestCell = floor( pixel.sub( node._sampleOffset ).div( node._stride ).add( 0.5 ) );

			// Yakınlık: σ = 0,75 hücre.
			const sigma = node._stride.mul( 0.75 );
			const twoSigmaSquared = sigma.mul( sigma ).mul( 2 );

			const sum = vec4( 0 ).toVar();
			const weightSum = float( 0 ).toVar();

			for ( let dy = - 1; dy <= 1; dy ++ ) {

				for ( let dx = - 1; dx <= 1; dx ++ ) {

					const cell = nearestCell.add( vec2( dx, dy ) );
					const insideGrid = cell.greaterThanEqual( vec2( 0 ) ).all().and( cell.lessThan( node._cellResolution ).all() );

					const samplePixel = cellSamplePixel( node, cell );
					const cellValue = node._cellTexture.sample( cell.add( 0.5 ).div( node._cellResolution ) );
					const sampleSurface = readSurface( node, samplePixel.add( 0.5 ).div( node._fullResolution ) );

					const offset = samplePixel.sub( pixel );
					const closeness = exp( dot( offset, offset ).div( twoSigmaSquared ).negate() );
					const usable = insideGrid.and( cellValue.a.greaterThanEqual( 0 ) );

					const weight = usable.select( closeness.mul( surfaceSimilarity( center, sampleSurface ) ), 0 );

					sum.addAssign( cellValue.mul( weight ) );
					weightSum.addAssign( weight );

				}

			}

			result.assign( weightSum.greaterThan( MIN_WEIGHT ).select( sum.div( weightSum ), vec4( 0, 0, 0, NO_DATA ) ) );

		} );

		return result;

	} )();

}
